import { Dictionary, isEmpty, keyBy, xor } from 'lodash';

import { updateAssessment } from '@/data/services/assessmentServices';
import { Assessment } from '@/models/Assessment';
import Evaluation from '@/models/Evaluation';
import alert from '@/utils/shared/alert';
import { findCloneByOrder } from '@/utils/evaluation/findCloneByOrder';
import { getErrorMessage } from '@/utils/getErrorMessage';
import useTemplatePropagation from './useTemplatePropagation';

type SlaveLessonBlocksInput = {
  slaveEvaluationId: number;
  lessonBlockIds: number[];
};

type UseSlaveAssessmentsProps = {
  evaluation: Evaluation;
};

const sameLessonBlocks = (a: number[], b: number[]) => isEmpty(xor(a, b));

const mergeAssessments = (
  assessments: Assessment[],
  clone: Assessment,
  newAssessment: Assessment,
) =>
  assessments.map(current =>
    current.id === clone.id ? newAssessment : current,
  );

const getAssessmentFromEvaluation = (
  evaluationsMap: Dictionary<Evaluation>,
  slaveEvaluationId: number,
  order: number,
) => {
  const slaveEvaluation = evaluationsMap[slaveEvaluationId];
  const assessment = slaveEvaluation
    ? findCloneByOrder(slaveEvaluation.assessments, order)
    : undefined;
  return { slaveEvaluation, assessment };
};

const useSlaveAssessments = ({ evaluation }: UseSlaveAssessmentsProps) => {
  const { propagate, slaveEntries, cacheManager, isLoading } =
    useTemplatePropagation({ evaluation });

  const updateAssessmentClone = async (
    row: SlaveLessonBlocksInput,
    evaluationsMap: Dictionary<Evaluation>,
    order: number,
  ) => {
    const { assessment, slaveEvaluation } = getAssessmentFromEvaluation(
      evaluationsMap,
      row.slaveEvaluationId,
      order,
    );
    if (!assessment || !slaveEvaluation) return;
    const updated = await updateAssessment(slaveEvaluation.id, assessment.id, {
      lessonBlockIds: row.lessonBlockIds,
    });

    cacheManager.update({
      ...slaveEvaluation,
      assessments: mergeAssessments(
        slaveEvaluation.assessments,
        assessment,
        updated,
      ),
    });
  };

  const updateAssessmentsSlaves = async (
    rows: SlaveLessonBlocksInput[],
    slaves: Evaluation[],
    order: number,
  ) => {
    const slavesMap = keyBy(slaves, 'id');
    const results = await Promise.allSettled(
      rows.map(row => updateAssessmentClone(row, slavesMap, order)),
    );

    const firstError = results.find(
      (r): r is PromiseRejectedResult => r.status === 'rejected',
    );

    if (firstError) {
      alert.error(getErrorMessage(firstError.reason));
    }
  };

  const getSlavesChanged = (
    lessonBlocksBySlave: SlaveLessonBlocksInput[],
    masterOrder: number,
  ) => {
    const slaveEvaluationsMap = keyBy(
      slaveEntries.map(({ slave }) => slave),
      'id',
    );
    return lessonBlocksBySlave.filter(current => {
      const { assessment } = getAssessmentFromEvaluation(
        slaveEvaluationsMap,
        current.slaveEvaluationId,
        masterOrder,
      );
      return (
        !!assessment &&
        !sameLessonBlocks(assessment.lessonBlockIds, current.lessonBlockIds)
      );
    });
  };

  const propagateAndPatch = async (
    masterAssessment: Assessment,
    rows: SlaveLessonBlocksInput[],
  ) => {
    const updatedSlaves = await propagate();
    if (!updatedSlaves || rows.length === 0) return;

    await updateAssessmentsSlaves(rows, updatedSlaves, masterAssessment.order);
  };

  const createSlaves = (
    masterAssessment: Assessment,
    lessonBlocksBySlave: SlaveLessonBlocksInput[],
  ) =>
    propagateAndPatch(
      masterAssessment,
      lessonBlocksBySlave.filter(row => row.lessonBlockIds.length > 0),
    );

  const updateSlaves = (
    masterAssessment: Assessment,
    lessonBlocksBySlave: SlaveLessonBlocksInput[],
  ) =>
    propagateAndPatch(
      masterAssessment,
      getSlavesChanged(lessonBlocksBySlave, masterAssessment.order),
    );

  return {
    slaveEntries,
    isLoading,
    createSlaves,
    updateSlaves,
  };
};

export default useSlaveAssessments;
